import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const TrendLine = ({ data = [] }) => {
    const containerRef = useRef();
    const svgRef = useRef();
    const tooltipRef = useRef();

    useEffect(() => {
        if (!Array.isArray(data) || !data.length || !containerRef.current) return;

        const container = containerRef.current;
        const { width } = container.getBoundingClientRect();
        const height = 300;
        const margin = {
            top: 20, right: 20, bottom: 50, left: 60,
        };
        const innerWidth = width - margin.left - margin.right;
        const innerHeight = height - margin.top - margin.bottom;

        const grouped = d3.groups(data, d => d.row);
        const periods = [...new Set(data.map(d => d.column))];

        const x = d3.scalePoint()
            .domain(periods)
            .range([0, innerWidth]);

        const allValues = data.map(d => d.value);
        const y = d3.scaleLinear()
            .domain([d3.min(allValues), d3.max(allValues)])
            .nice()
            .range([innerHeight, 0]);

        const svg = d3.select(svgRef.current).html('');
        const tooltip = d3.select(tooltipRef.current);

        const chart = svg
            .attr('width', width)
            .attr('height', height)
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        // Axes
        chart.append('g')
            .attr('transform', `translate(0,${innerHeight})`)
            .call(d3.axisBottom(x));
        chart.append('g').call(d3.axisLeft(y));

        const line = d3.line()
            .defined(d => typeof d.value === 'number')
            .x(d => x(d.column))
            .y(d => y(d.value));

        grouped.forEach(([mdrm, values]) => {
            const color = values[0]?.color || '#333';
            const ordered = periods.map(p => values.find(d => d.column === p) || { column: p, value: NaN });

            const path = chart.append('path')
                .datum(ordered)
                .attr('fill', 'none')
                .attr('stroke', color)
                .attr('stroke-width', 2)
                .style('cursor', 'pointer')
                .attr('d', line)
                .on('mouseover', event => {
                    path.attr('stroke-width', 4);

                    const [xPos, yPos] = d3.pointer(event);
                    const tooltipHtml = `
                        <strong>${mdrm}</strong><br/>
                        ${ordered.map(d => (d.value !== undefined && !isNaN(d.value)
        ? `${d.column}: ${d.value.toFixed(2)}%`
        : '')).filter(Boolean).join('<br/>')}
                    `;

                    tooltip
                        .style('opacity', 1)
                        .html(tooltipHtml)
                        .style('left', `${xPos + margin.left + 10}px`)
                        .style('top', `${yPos + margin.top - 40}px`);
                })
                .on('mousemove', event => {
                    const [xPos, yPos] = d3.pointer(event);
                    tooltip
                        .style('left', `${xPos + margin.left + 10}px`)
                        .style('top', `${yPos + margin.top - 40}px`);
                })
                .on('mouseout', () => {
                    path.attr('stroke-width', 2);
                    tooltip.style('opacity', 0);
                });

            // Points
            chart.selectAll(`.point-${mdrm}`)
                .data(ordered.filter(d => d.value !== undefined && !isNaN(d.value)))
                .enter()
                .append('circle')
                .attr('cx', d => x(d.column))
                .attr('cy', d => y(d.value))
                .attr('r', 3)
                .attr('fill', color)
                .attr('stroke', '#fff')
                .attr('stroke-width', 0.8)
                .style('pointer-events', 'none');
        });
    }, [data]);

    return (
        <div ref={containerRef} style={{ width: '100%', height: '300px', position: 'relative' }}>
            <svg ref={svgRef} />
            <div
                ref={tooltipRef}
                style={{
                    position: 'absolute',
                    opacity: 0,
                    background: '#333',
                    color: '#fff',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    pointerEvents: 'none',
                    fontSize: '12px',
                    maxWidth: '250px',
                    zIndex: 10,
                    whiteSpace: 'pre-line',
                }}
            />
        </div>
    );
};

export default TrendLine;
